import { Component, type ErrorInfo } from "react";
import { reportFrontendError } from "@/frontend/lib/error-reporter";
import { ErrorFallback } from "./error-fallback";
import type { ErrorBoundaryProps, ErrorBoundaryState } from "@/frontend/types/errors";

/**
 * Outer React error boundary that catches errors thrown outside the TanStack
 * Router tree. Route-scoped errors are caught by the router's own boundary via
 * `defaultErrorComponent` in router.tsx; this handles anything above it.
 * Wraps the root once in main.tsx.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    reportFrontendError({
      message: error.message,
      stack: error.stack ?? "",
      componentStack: info.componentStack ?? undefined,
    });
  }

  override render() {
    if (this.state.hasError) return this.props.fallback ?? <ErrorFallback />;
    return this.props.children;
  }
}
